import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { HouseholdDetail } from "@guestbook/shared";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/shared/components/ui/AlertDialog";
import {
  useDeleteHousehold,
  useHousehold,
  useUpdateHousehold,
} from "@/shared/lib/households";
import { EditHouseholdMembersDialog } from "./EditHouseholdMembersDialog";

function householdName(hh: HouseholdDetail): string {
  return hh.displayNameCustom ?? "Unnamed household";
}

export function HouseholdDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useHousehold(id);
  const del = useDeleteHousehold();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (query.isLoading) {
    return (
      <Section spacing="md">
        <Container size="4xl">
          <p className="type-body-base text-text-muted">Loading…</p>
        </Container>
      </Section>
    );
  }

  if (query.isError) {
    const notFound = query.error.status === 404;
    return (
      <Section spacing="md">
        <Container size="4xl">
          <div className="flex flex-col gap-4">
            <h1 className="type-heading-lg text-text">
              {notFound ? "Household not found" : "Couldn't load household"}
            </h1>
            <p className="type-body-base text-text-muted">
              {notFound
                ? "This household may have been deleted or never existed."
                : query.error.message}
            </p>
            <div>
              <Button asChild variant="outline">
                <Link to="/app/contacts/households">Back to households</Link>
              </Button>
            </div>
          </div>
        </Container>
      </Section>
    );
  }

  const household = query.data!;
  const name = householdName(household);

  function handleDelete() {
    if (!id) return;
    del.mutate(id, { onSuccess: () => navigate("/app/contacts/households") });
  }

  return (
    <Section spacing="md">
      <Container size="4xl">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Link
              to="/app/contacts/households"
              className="type-label-sm tracking-wider uppercase text-text-muted hover:text-text"
            >
              ← Back to households
            </Link>
            <div className="flex items-baseline justify-between gap-4">
              <h1 className="type-heading-lg text-text">{name}</h1>
              <div className="flex items-center gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to={`/app/contacts/households/${household.id}/edit`}>Edit</Link>
                </Button>
                <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost-danger" size="sm">
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        The household is removed. Member contacts remain, but lose
                        their household assignment.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogAction asChild>
                        <Button
                          variant="destructive"
                          onClick={handleDelete}
                          disabled={del.isPending}
                        >
                          {del.isPending ? "Deleting…" : "Delete household"}
                        </Button>
                      </AlertDialogAction>
                      <AlertDialogCancel asChild>
                        <Button variant="ghost">Cancel</Button>
                      </AlertDialogCancel>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>

          <HouseholdSummary household={household} />
          <HouseholdMembers household={household} />
        </div>
      </Container>
    </Section>
  );
}

function HouseholdSummary({ household }: { household: HouseholdDetail }) {
  const a = household.address;
  const hasAddress = Boolean(
    a &&
      (a.line1 ||
        a.line2 ||
        a.city ||
        a.stateCode ||
        a.stateName ||
        a.postalCode ||
        a.countryCode ||
        a.countryName),
  );
  return (
    <section className="flex flex-col gap-4">
      <h2 className="type-heading-sm text-text">Mailing address</h2>
      {hasAddress ? (
        <address className="not-italic type-body-base text-text whitespace-pre-line">
          {[a?.line1, a?.line2].filter(Boolean).join("\n")}
          {a?.line1 ? "\n" : ""}
          {[a?.city, a?.stateCode ?? a?.stateName, a?.postalCode]
            .filter(Boolean)
            .join(", ")}
          {a?.countryName ?? a?.countryCode
            ? `\n${a?.countryName ?? a?.countryCode}`
            : ""}
        </address>
      ) : (
        <p className="type-body-base text-text-muted">No address on file.</p>
      )}
    </section>
  );
}

function HouseholdMembers({ household }: { household: HouseholdDetail }) {
  const update = useUpdateHousehold(household.id);
  const [editOpen, setEditOpen] = useState(false);

  function removeMember(contactId: string) {
    update.mutate({ members: { remove: [contactId] } });
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="type-heading-sm text-text">Members</h2>
        <div className="flex items-baseline gap-4">
          <p className="type-body-sm text-text-muted">
            {household.memberCount} {household.memberCount === 1 ? "member" : "members"}
          </p>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            Edit members
          </Button>
        </div>
      </div>

      {household.members.length === 0 ? (
        <p className="type-body-base text-text-muted">No members yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {household.members.map((m) => (
            <li
              key={m.id}
              className="flex items-center justify-between gap-4 px-4 py-3 rounded-lg glass-surface-subtle"
            >
              <Link
                to={`/app/contacts/${m.id}`}
                className="type-body-base text-text underline-offset-4 hover:underline"
              >
                {m.name || "Unnamed contact"}
              </Link>
              <Button
                variant="ghost-danger"
                size="sm"
                onClick={() => removeMember(m.id)}
                disabled={update.isPending}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      {update.error ? (
        <p className="type-body-sm text-danger-strong mx-4">{update.error.message}</p>
      ) : null}

      <EditHouseholdMembersDialog
        household={household}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </section>
  );
}
